import _ from 'underscore';
import $ from 'jquery';
import {
  upgradeCampaignsToPMaxCall,
  fetchCampaignUpgradeData,
  upgradeSuccess,
  upgradeFailure,
  onGenericError,
  clearCache
} from '@bingads-webui-campaign-react/campaign-upgrade-tool';
import { withUiBlock } from '@bingads-webui/uiblocker';

import { notificationApi } from '../api';
import { INotification, TargetComponent, NotificationLevel, DismissMode, ScopeLevel, IContext } from '../../types';
import { i18nMap, shouldRender, isAvailable, isBillingLinkPermission } from '../../utils';


export interface IPmaxCampaignUpgradeNotification{
  HasPmaxCampaignUpgrade: boolean,
}

export class PmaxCampaignUpgradeNotification {
  context: IContext;
  i18n: any;
  availableScopes: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = context.getNewI18n();
    this.availableScopes = [ScopeLevel.account];
  }

  async getEligibleCampaignNum(params: any): Promise<any> {
    return fetchCampaignUpgradeData(params);
  }

  async upgradeCampaignsToPMax(params: any): Promise<any> {
    return upgradeCampaignsToPMaxCall(params);
  }

  async getPmaxCampaignUpgradeNotifications() {
    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    const {
      PerformanceMaxCampaignsPhaseTwoCampaignUpgradeToolNotification,
    } = this.context.getCurrentPermissions().dynamic;

    if (!PerformanceMaxCampaignsPhaseTwoCampaignUpgradeToolNotification) {
      return [];
    }

    if (!isAvailable(this.context.getCurrentScope(), this.availableScopes)) {
      return [];
    }

    if (!shouldRender(ScopeLevel.account, this.context.getChangeScope())) {
      return [];
    }

    const pmaxCampaignUpgradeNotification: IPmaxCampaignUpgradeNotification = await this.getNotifications();
    const notifications: INotification[] = [];

    if (this.hasAnyPmaxCampaignUpgradeNotifications(pmaxCampaignUpgradeNotification)) {
      const { aid, cid } = this.context.getCurrentScope().values;

      const payload = {
        Campaigns: [{
          ParentId: aid,
          EntityId: -1,
        }],
      };

      const onUpgradeApiError = (response) => {
        const errors = _.result(response, 'errors', []);
        if (_.isEmpty(errors)) {
          onGenericError(this.i18n);
        } else {
          notificationApi.add({
            id: 'campaignUpgradeApiError',
            condition: true,
            severity: NotificationLevel.Error,
            message: this.i18n.getString(upgradeFailure),
            target: TargetComponent.FloatingBanner,
          });
        }
      };

      notifications.push({
        id: 'PmaxCampaignUpgrade',
        condition: true,
        target: TargetComponent.UnifiedQueue,
        dismissMode: DismissMode.session,
        severity: NotificationLevel.Info,
        scopeLevel: ScopeLevel.account,
        availableScopes: this.availableScopes,
        title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Pmax_CampaignUpgrade_Title),
        message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Pmax_CampaignUpgrade_Message),
        extraOptions: {
          dialogProps: {
            dialogTitle: this.i18n.getString(_TL_('Are you sure you want to upgrade all smart shopping campaigns to Performance Max campaigns?')),
            dialogText: this.i18n.getString(_TL_('Once you confirm, the upgrade process will continue until it completes and cannot be discontinued.')),
            dialogConfirm: this.i18n.getString(_TL_('Yes')),
            dialogCancel: this.i18n.getString(_TL_('Cancel')),
            dialogAction: (removeNotification) => {
              this.upgradeCampaignsToPMax({ cid, aid, payload })
                .then((response) => {
                  const updatedCampaigns = response.value;
                  notificationApi.add({
                    id: 'campaignUpgradeApiSuccess',
                    condition: true,
                    severity: NotificationLevel.Info,
                    message: this.i18n.getString(upgradeSuccess, { count: updatedCampaigns.length }),
                    target: TargetComponent.FloatingBanner,
                  });
                  if (_.isFunction(removeNotification)) removeNotification();
                  clearCache('Campaign');
                })
                .catch(onUpgradeApiError)
                .finally(withUiBlock($('.notification-queue')));
            },
          },
        },
        actionList: [
          {
            text: this.i18n.getString(i18nMap.Notification_Action_Apply),
            callbackType: 'openDialog',
            extraClassNames: ['campaign-upgrade-apply-button'],
          },
        ],
      });
    }

    return notifications;
  }

  async getNotifications(): Promise<IPmaxCampaignUpgradeNotification> {
    const { aid, cid } = this.context.getCurrentScope().values;

    const eligibleUpgradeCampaigns = await this.getEligibleCampaignNum({
      cid,
      aid,
      params: { $filter: "(CampaignMigrationStatus eq Enum.CampaignMigrationStatus'AvailableForUpgrade')" },
      payload: {
        gridDataParameters: {
          DateRange: {
          // Just want to get campaign upgrade status, don't care BI data, so pass dummy date ranges.
            StartDate: '2024-10-29',
            EndDate: '2024-10-29',
          },
        },
      },
    });

    if (_.result(eligibleUpgradeCampaigns, '@odata.count') > 0) {
      return { HasPmaxCampaignUpgrade: true } as IPmaxCampaignUpgradeNotification;
    }
    return { HasPmaxCampaignUpgrade: false } as IPmaxCampaignUpgradeNotification;
  }

  private hasAnyPmaxCampaignUpgradeNotifications(pmaxCampaignUpgradeNotification: IPmaxCampaignUpgradeNotification): boolean {
    return pmaxCampaignUpgradeNotification.HasPmaxCampaignUpgrade;
  }
}
